import styles from '@/components/static/StudyMaterialsGuidePage/StudyMaterialsGuidePage.module.scss'
import React, { ReactElement } from 'react'
import { BaseGuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponent/BaseGuideComponent'
import { GuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/GuideComponent.types'
import { Screenshot } from '@/components/static/images/Screenshot/Screenshot'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import Tooltip from 'antd/lib/tooltip'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { ArrowDownOutlined } from '@ant-design/icons'
import { StudyMaterialsGuideExternalLink } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/StudyMaterialsGuideExternalLink/StudyMaterialsGuideExternalLink'
import { BackToTop } from '@/components/static/BackToTop/BackToTop'
import { ClickableArrow } from '@/components/ClickableArrow/ClickableArrow'
import { OwlPageHeader } from '@/components/OwlPageHeader/OwlPageHeader'
import { NotecardOwl } from '@/components/static/images/owlBranding/notecardOwl'
import { BaseGuideComponentNoImage } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponentNoImage/BaseGuideComponentNoImage'

export const NotecardsGuidePage: React.FC<GuideComponent> = (
    props
): ReactElement => {
    return (
        <div className={styles.studyMaterialsGuidePageBackground}>
            <BackToTop isLoggedIn={props.isLoggedIn} />
            <div className={styles.studyMaterialsGuidePage}>
                <div className={styles.headerContainer}>
                    <div className={reusableCssClass.centerChildrenVertically}>
                        <Tooltip
                            title={'Return to Overview'}
                            placement={'right'}
                        >
                            <ClickableArrow
                                orientation={'left'}
                                linkDestination={
                                    UiLayoutConstant.STUDY_MATERIALS_GUIDE_PAGE
                                }
                            />
                        </Tooltip>
                    </div>
                    <div className={styles.owlHeader}>
                        <OwlPageHeader
                            text={'Notecards'}
                            owlComponent={<NotecardOwl />}
                        />
                    </div>
                </div>
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Summary</div>
                            <ul>
                                <li>
                                    Notecards have historically comprised ~40%
                                    of the points on each exam, and the
                                    information learned by doing notecards
                                    extends meaningfully to the remaining ~60%.
                                </li>
                                <li>
                                    It&apos;s recommended that you prioritize
                                    lessons and reviews as soon as they are
                                    available in order to learn at the fastest
                                    speed possible and prevent getting
                                    &quot;stuck&quot; waiting on reviews.
                                </li>
                                <li>
                                    Our notecard mastery algorithm uses your
                                    feedback to automatically choose the best
                                    notecards for you.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardWithReviewAndMistakes.webp'
                                }
                                alt={'screenshot of notecard deck in action'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Philosophy</div>
                            Notecard-style problems on the exam can be solved
                            through a simple two-step process:
                            <ol>
                                <li>
                                    Identifying the notecard associated with the
                                    problem
                                </li>
                                <li>
                                    Reproducing the back of the notecard on the
                                    exam
                                </li>
                            </ol>
                            Spending time memorizing notecards is one of the
                            highest value study activities you can do. This is
                            because:
                            <ul>
                                <li>
                                    <b>
                                        Answering a notecard question takes
                                        significantly less time per point than
                                        any other type of question
                                    </b>{' '}
                                    - there are no silly math mistakes, or a
                                    need to come up with a thoughtful argument.
                                    Since all points are equal, it makes sense
                                    to do these first.
                                </li>
                                <li>
                                    <b>
                                        Notecard questions are the easiest type
                                        of question
                                    </b>{' '}
                                    - you either know it or you don&apos;t.
                                </li>
                                <li>
                                    <b>
                                        Relative to math questions, it is much
                                        harder to get points on a notecard
                                        question if you don&apos;t have the
                                        notecard memorized
                                    </b>{' '}
                                    - with math questions, one can often
                                    &quot;derive&quot; answers even if they
                                    don&apos;t have the exact formula memorized.
                                    Getting partial credit on a notecard
                                    question you don&apos;t have memorized is
                                    incredibly difficult.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardFrontScreenshot.webp'
                                }
                                alt={'screenshot of a notecard in action'}
                            />
                            <div className={`${styles.flipIconContainer}`}>
                                <div className={styles.iconContainer}>
                                    <ArrowDownOutlined />
                                </div>
                                FLIP!
                                <div className={styles.iconContainer}>
                                    <ArrowDownOutlined />
                                </div>
                            </div>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardBackScreenshot.webp'
                                }
                                alt={'screenshot of a notecard in action'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponentNoImage
                    text={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                How does it work?
                            </div>
                            <p>
                                The system is fundamentally based on the concept
                                of{' '}
                                <a
                                    href={
                                        'https://thrive.arizona.edu/news/adding-spaced-repetition-your-study-toolkit#:~:text=Spaced%20repetition%20is%20the%20act,with%20increasing%20intervals%20between%20them.'
                                    }
                                    className={reusableCssClass.clickableText}
                                >
                                    spaced repetition
                                </a>
                                . That is, the better you know a notecard, the
                                less often you will review it.
                            </p>
                            <p>
                                Each notecard has a &quot;mastery&quot;
                                associated with it, which determines how
                                frequently you should revew it.
                            </p>
                            Our notecard mastery levels are:{' '}
                            <ul>
                                <li>
                                    <b>New</b> - Not seen yet
                                </li>
                                <li>
                                    <b>Apprentice1</b> - Completed lesson
                                </li>
                                <li>
                                    <b>Apprentice2</b>
                                </li>
                                <li>
                                    <b>Apprentice3</b>
                                </li>
                                <li>
                                    <b>Apprentice4</b>
                                </li>
                                <li>
                                    <b>Guru1</b>
                                </li>
                                <li>
                                    <b>Guru2</b>
                                </li>
                                <li>
                                    <b>
                                        <u>Master</u>
                                    </b>{' '}
                                    - Target mastery level to be successful on
                                    exam
                                </li>
                                <li>
                                    <b>Enlightened</b>
                                </li>
                                <li>
                                    <b>Burned</b> - You will never forget this
                                </li>
                            </ul>
                            <ol>
                                <li>
                                    When you see a notecard in a lesson, the
                                    notecard has mastery level <b>New</b>. Upon
                                    completing the lesson, your notecard moves
                                    to <b>Apprentice1</b>.
                                </li>
                                <li>
                                    Afterwards, each time you select &ldquo;I
                                    got it&ldquo; on a notecard in review, the
                                    mastery level increases. For example, if you
                                    start in <b>Apprentice1</b> and answer the
                                    notecard correctly each time in a review
                                    session, you will progress to <b>Guru1</b>
                                </li>
                                <li>
                                    If you &ldquo;sort of&ldquo; answer
                                    correctly (&ldquo;I ALMOST got it&ldquo;),
                                    you still at the same level. If you answer
                                    incorrectly, you go down 1 level.
                                </li>
                                <li>
                                    The higher the mastery level, the less
                                    frequently you will see the notecard. For
                                    example, you will see a notecard in{' '}
                                    <b>Apprentice1</b> every 4 hours but will
                                    see a notecard in <b>Guru2</b> every 2
                                    weeks.
                                </li>
                                <li>
                                    The goal is to reach <b>MASTER</b> status on
                                    all notecards. <b>BURNED</b> means the
                                    notecard is burned into your memory forever.
                                </li>
                            </ol>
                            <p>
                                The specific implementation of this system is
                                heavily borrowed from and inspired by{' '}
                                <a
                                    href={'https://wanikani.com'}
                                    className={reusableCssClass.clickableText}
                                    target={'_blank'}
                                    rel="noreferrer"
                                >
                                    wanikani
                                </a>
                                , a spaced repetition system for native English
                                speakers to learn to read Japanese kanji. If
                                you&apos;d like to learn more about the details
                                and motivation behind the system, they have
                                several great blog posts on these topics{' '}
                                <a
                                    href={'https://knowledge.wanikani.com/'}
                                    className={reusableCssClass.clickableText}
                                    target={'_blank'}
                                    rel="noreferrer"
                                >
                                    here
                                </a>
                                .
                            </p>
                            <p>
                                Our system is broken down into the following
                                sections:
                                <ul>
                                    <li>Lessons</li>
                                    <li>Reviews</li>
                                    <li>Recent Mistakes</li>
                                    <li>Extra Practice</li>
                                </ul>
                            </p>
                        </div>
                    }
                />
                <BaseGuideComponent
                    shouldStackImagesAndBullets={false}
                    bulletSide={'left'}
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardSetScreenshot.webp'
                                }
                                alt={'screenshot of notecard deck in action'}
                            />
                        </div>
                    }
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                How can I be successful?
                            </div>
                            <p>
                                Each of our course offerings has more than 400
                                notecards. Going into the exam with a high
                                confidence to pass requires being knowledgeable
                                about every single notecard (otherwise that
                                notecard wouldn&apos;t be part of our package).
                            </p>
                            <p>
                                The system presented is designed to enable
                                students to learn every single notecard in as
                                short a time as possible, with as little effort
                                as possible (spoiler: it will still take
                                considerable effort!). In order to be
                                successful, all you need to do is this:
                            </p>
                            <ol>
                                <li>Do the available lessons.</li>
                                <li>Do your reviews.</li>
                                <li>
                                    Do them every day. Yes, even on weekends and
                                    holidays.
                                </li>
                            </ol>
                            <p>
                                Personally, a daily strategy I have found to
                                work really well for me is the following,
                                beginning first thing in the morning when I wake
                                up:
                            </p>
                            <ol>
                                <li>
                                    Do the available reviews (until there are
                                    none remaining).
                                </li>
                                <li>Do one round of lessons (5 notecards).</li>
                                <li>
                                    Review recent mistakes (one time through).
                                </li>
                                <li>
                                    Then, I take a break for ~4 hours to do
                                    other things.
                                </li>
                                <li>
                                    When I come back, I repeat this process, so
                                    I can catch the first round of lessons as
                                    they go into review, as well as do another
                                    round of lessons, and work on my mistakes
                                    even further.
                                </li>
                                <li>
                                    I then try to repeat this entire process one
                                    more time, so I have done my reviews 3x in
                                    one day as well as 3x rounds of lessons (for
                                    a total of 15 new notecards learned).
                                </li>
                            </ol>
                        </div>
                    }
                />

                <BaseGuideComponentNoImage
                    text={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                How long does it take to finish?
                            </div>
                            <ul>
                                <li>
                                    If you learn 15 new notecards per day, have
                                    a high success rate, and do reviews quickly,
                                    you can master most notecards in 3 months.
                                </li>
                                <li>
                                    However, this is an aggressive schedule, and
                                    most students will find it takes closer to 4
                                    months.
                                </li>
                                <li>
                                    Our system recommends learning 15 new
                                    notecards per day by default. However, if
                                    you start to fall behind, you will be asked
                                    to complete more new notecards per day, so
                                    that you can learn the last new notecard 1
                                    month before the exam (and therefore give
                                    you a month to master it, along with the
                                    other notecards).
                                </li>
                            </ul>
                        </div>
                    }
                />

                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Lessons</div>
                            <ul>
                                <li>
                                    Lessons are the only way to have new
                                    notecards added to your review pile (to
                                    begin memorizing them via spaced
                                    repetition).
                                </li>
                                <li>
                                    Learning a new notecard can take a
                                    considerable amount of effort (especially
                                    when trying to retain previous notecards),
                                    so we don&apos;t recommend trying to learn
                                    more than 15 new notecards per day.
                                </li>
                                <li>
                                    Lessons are automatically batched to a
                                    maximum of 5 notecards at a time.
                                </li>
                                <li>
                                    A lesson consists of the following steps:
                                    <ol>
                                        <li>
                                            <b>Initial review:</b> read through
                                            each notecard&apos;s front and back,
                                            study its mnemonic, and take your
                                            own notes.
                                        </li>
                                        <li>
                                            <b>Quiz:</b> you will be asked a
                                            question directly pertaining to one
                                            of the notcards. Answer, then click
                                            next and assess your answer (got it
                                            / did not get it). If you do not get
                                            it on your first try, you will be
                                            asked the same question later on.
                                        </li>
                                        <li>
                                            Once you mark &ldquo;got it&ldquo;
                                            on all 5 questions, these notecards
                                            will be moved to your review pile
                                            and can be reviewed again in a
                                            couple of hours.
                                        </li>
                                        <li>
                                            After finishing a lesson batch,
                                            don&apos;t forget that there might
                                            be more lessons to complete today!
                                        </li>
                                    </ol>
                                    <li>
                                        Even after all lessons for a given day
                                        are completed, you still have the option
                                        to complete more if you are feeling up
                                        for it.
                                    </li>
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={'/studyMaterialsGuide/notecardLesson.webp'}
                                alt={'screenshot of notecard in lesson'}
                            />
                            <br />
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardLessonQuiz.webp'
                                }
                                alt={'screenshot of quiz in lesson'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />

                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Reviews</div>
                            <ul>
                                <li>
                                    Reviews are the only way for you to increase
                                    your mastery state past apprentice.
                                </li>
                                <li>
                                    Your mastery state cannot go below
                                    apprentice.
                                </li>
                                <li>
                                    The sooner you complete your reviews after
                                    they unlock, the better. Reviews are timed
                                    so that they unlock just before you are
                                    likely to forget the material.
                                </li>
                                <li>
                                    You can use the Review Forecast to identify
                                    when you will next have a large batch of
                                    reviews to complete.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardReviewForecast.webp'
                                }
                                alt={
                                    'screenshot of notecard review forecast schedule'
                                }
                            />
                            <br />
                            <Screenshot
                                src={'/studyMaterialsGuide/notecardReview.webp'}
                                alt={'screenshot of notecard review'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />

                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Recent Mistakes</div>
                            <ul>
                                <li>
                                    Notecards you have gotten incorrect in the
                                    past 24 hours will show up here.
                                </li>
                                <li>
                                    Utilizing this feature is entirely optional,
                                    but if you have extra time, this is a great
                                    way to target practicing notecards that may
                                    not be sticking in your head as easily as
                                    others.
                                </li>
                                <li>
                                    Grading yourself here will have no effect on
                                    your mastery state.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardRecentMistakeOverview.webp'
                                }
                                alt={'screenshot of recent mistake overview'}
                            />
                            <br />
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardRecentMistake.webp'
                                }
                                alt={'screenshot of recent mistake example'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />

                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Extra Practice</div>
                            <ul>
                                <li>
                                    Once you&apos;ve gained a bit of experience
                                    with the notecards, you may find value in
                                    exploring the custom deck builder to focus
                                    on the areas you have identified are
                                    valuable for you to study.
                                </li>
                                <li>
                                    If you want to add a specific notecard to a
                                    deck, click the &quot;Search & Add&quot;
                                    button at the bottom. You can use free-text
                                    search to find the problem based on what you
                                    remember of it.
                                </li>
                                <li>
                                    Use the history tool at the bottom of the
                                    dashboard to revisit old notecard decks. Our
                                    system tags decks in various ways to help
                                    you tell them apart.
                                </li>
                                <li>
                                    Use the notecard dashboard analytics along
                                    with the custom notecard deck build tool to
                                    spend more time on notecards you have not
                                    yet mastered.
                                </li>
                                <li>
                                    Grading yourself here will have no effect on
                                    your mastery state.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardSetScreenshot.webp'
                                }
                                alt={'screenshot of notecard deck in action'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />

                <BaseGuideComponentNoImage
                    text={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>How to Grade</div>
                            <ol>
                                <li>
                                    Do not flip the notecard over until you have
                                    fully attempted to answer the notecard.
                                </li>
                                <li>
                                    Use the scratchpad notes provided, or pen
                                    and paper, to answer the notecard.{' '}
                                    <StudyMaterialsGuideExternalLink
                                        href={
                                            'https://bigdesignevents.com/2011/08/26/how-your-multiple-senses-help-you-to-remember/#:~:text=When%20you%20use%20multiple%20senses,the%20memory%20for%20later%20retrieval.'
                                        }
                                        text={
                                            'When you use multiple senses to encode a memory, you increase your ability to encode the memory for later retrieval.'
                                        }
                                    />{' '}
                                    For additional retention, say your answer
                                    out loud to yourself.
                                </li>
                                <li>
                                    If you can&apos;t quite remember the
                                    notecard, use the hint(s) provided. They
                                    will commonly tip you off on how many items
                                    are expected on the back (which can help you
                                    gauge if you have listed 75% of the items in
                                    the list!).
                                </li>
                                <li>
                                    Once you have finished attempting the
                                    notecard, flip it over. Read through the
                                    back carefully, put yourself in the shoes of
                                    an exam grader, and be honest about what
                                    percent of the notecard you successfully
                                    listed. Best practice for grading is as
                                    follows:
                                </li>
                                <ul>
                                    <li>
                                        Mark &quot;I Know This&quot; (aka
                                        succeeded) on a notecard if you can
                                        faithfully reproduce 75% of the items on
                                        the back (or at least explain them in
                                        your own words). Your mastery state will
                                        increase.
                                    </li>
                                    <li>
                                        Mark &quot;Need To Review&quot; if you
                                        knew less than half of the items on the
                                        back. Your mastery state will decrease.
                                    </li>
                                    <li>
                                        If you get somewhere between 50% and
                                        75%, mark &quot;I ALMOST got it&quot;.
                                        Your mastery state will stay the same.
                                    </li>
                                    <li>
                                        Note that grading only affects your
                                        mastery state in review mode, and does
                                        not have any effect when done in recent
                                        mistakes or extra practice mode.
                                    </li>
                                </ul>

                                <li>
                                    After grading yourself, click &quot;See
                                    More&quot; for a quick link to additional
                                    information to help you remember this
                                    notecard in the future such as:
                                    <ul>
                                        <li>Mnemonic</li>
                                        <li>
                                            Link to the outline where the
                                            notecard came from
                                        </li>
                                        <li>Hints</li>
                                    </ul>
                                </li>
                                <li>
                                    Takes notes for this specific problem to
                                    help you remember it in the future (such as
                                    your own custom mnemonic!). Notes will be
                                    forever associated with this single
                                    notecard, so you can always refer back to
                                    them when reviewing the same notecard. Make
                                    sure you select &quot;Notes&quot;, not
                                    &quot;Scratchpad&quot;!
                                </li>
                            </ol>
                        </div>
                    }
                />

                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Categorization</div>
                            <p>
                                To aid with your studying, notecards are broken
                                down by the following dimensions:
                            </p>
                            <ol>
                                <li>
                                    <b>Topic</b>&nbsp;- Location within the
                                    syllabus. There are three tiers total within
                                    the{' '}
                                    <StudyMaterialsGuideExternalLink
                                        href={
                                            'https://www.soa.org/4ab6a3/globalassets/assets/files/edu/2023/spring/syllabi/ghdp.pdf'
                                        }
                                        text={'syllabus tree'}
                                    />
                                    : topic, subtopic (source), and chapter.
                                </li>
                                <li>
                                    <b>Mastery</b>&nbsp;- Based on your
                                    self-grading history with the notecard, we
                                    assign a mastery level to each notecard:{' '}
                                    <b>
                                        NEW {'>'} APPRENTICE {'>'} GURU {'>'}{' '}
                                        MASTER {'>'} ENLIGHTENED {'>'} BURNED
                                    </b>
                                </li>
                                <li>
                                    <b>Importance</b>&nbsp;- We have an
                                    extensive database of past exam questions,
                                    topics, and how they map to our notecards.
                                    Based on a combination of an in depth
                                    analysis of this data along with our
                                    instructors&apos; expert opinions, we have
                                    assigned an importance level to each
                                    notecard: high, medium, and low.
                                </li>
                                <li>
                                    <b>Categories</b>&nbsp;- Our instructors
                                    have handcrafted categories across notecards
                                    so that you can group similar notecards
                                    together. Want to focus only on definitions
                                    or formulas? This is where you can
                                    accomplish that.
                                </li>
                                <li>
                                    <b>Starred</b>&nbsp;- You can star / unstar
                                    individual notecards so that you can more
                                    easily come back to them later.
                                </li>
                            </ol>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardTopicMasteryScreenshot.webp'
                                }
                                alt={'screenshot of notecard topic mastery'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Extra Goodies</div>
                            <ul>
                                <li>
                                    Have a question about a specific notecard?
                                    Submit a comment to a notecard&apos;s
                                    dedicated comment thread and get a response
                                    from your instructor in less than 24 hours.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/notecardExtraGoodiesComments.webp'
                                }
                                alt={'screenshot of notecard comments'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Keyboard Shortcuts
                            </div>
                            <p>
                                When using notecards, various shortcuts are
                                available for your use:
                            </p>
                            <ul>
                                <li>
                                    ⬅ arrow key: Go back to the previous
                                    notecard
                                </li>
                                <li> ➡ arrow key: Go to the next notecard</li>
                                <li>spacebar: Flip the notecard</li>
                                <li>
                                    h key: Show <u>h</u>int
                                </li>
                                <li>1 key: Grade &apos;I got it&apos;</li>
                                <li>
                                    2 key: Grade &apos;I ALMOST got it&apos;
                                </li>
                                <li>
                                    3 key: Grade &apos;I need to review&apos;
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={'/studyMaterialsGuide/keyboard.webp'}
                                alt={'keyboard'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
            </div>
        </div>
    )
}
